import { roundMoneyPreview } from "@/features/currencies/utils"

/**
 * Pure helpers for the seat inputs on the invoice create/edit forms.
 *
 * The course line's Price field is the **per-seat unit price**: the
 * backend derives each course's total as `round(unit_price × seats)` and
 * sums the courses into the invoice amount, so the form sends the unit
 * verbatim and never rescales it. `scaleBySeats` therefore no longer
 * feeds the price default — it computes a DISPLAY-only "× N seats = <total>"
 * hint beside the Price input (see `CoursePriceFields`). Framework-agnostic
 * so both stay unit-testable in isolation.
 *
 * The seat count is NOT part of the course label — it renders as a
 * dedicated "Seats: N" line on the customer surfaces, sourced from the
 * stored `Invoice.seats`.
 */

/**
 * Upper bound on the seat count, mirroring `settings.INVOICE_MAX_SEATS`
 * (backend `src/invoicer/settings.py`).
 *
 * A MIRROR, not a served value: the backend re-validates every write with
 * its own `le=`, so a drifted copy here is a stale message, never a wrong
 * outcome. The same shape as `payment-modes.ts` mirroring the backend's
 * `PAYMENT_METHOD_SELECTABLE_MODES`. If the ceiling is ever revised, the
 * backend constant's comment points back at this file.
 *
 * See docs/design/bound-invoice-seats.md §4.
 */
export const MAX_SEATS = 1000

/**
 * Parse a seats form value (a text input) into a whole number >= 1.
 *
 * Any non-positive / non-integer / blank value falls back to `1`, so the
 * arithmetic helpers never produce `NaN` while the rep is mid-edit; the
 * Zod schema is what surfaces the validation error for a bad value.
 */
export function parseSeats(raw: string): number {
  const n = Number.parseInt(raw, 10)
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/**
 * Scale a per-seat unit price into the order **total** for `seats` seats.
 *
 * DISPLAY-only now: the Price field holds the per-seat unit and the backend
 * derives the total, so this is used solely to render the read-only
 * "× N seats = <total>" hint beside the Price input — it never writes back
 * into the form's price.
 *
 * Single-seat (`seats <= 1`) returns the unit price verbatim — the
 * headline regression guard, so a default-seat invoice keeps its exact
 * price string. For `seats > 1` the unit is rounded to whole major units
 * (billing precision) and multiplied. A blank or non-numeric unit price is
 * returned unchanged (the indicative-cohort "no quote" case, where the
 * price field stays empty).
 */
export function scaleBySeats(unitPrice: string, seats: number): string {
  if (unitPrice.trim() === "") return ""
  if (seats <= 1) return unitPrice
  // Reuse the shared half-up money rounder (mirrors the server's
  // round_money); it returns null for a non-numeric value, which we pass
  // through unchanged.
  const rounded = roundMoneyPreview(unitPrice)
  if (rounded === null) return unitPrice
  return String(rounded * seats)
}

/**
 * Roll the course rows up into the invoice's gross, in the **issue**
 * currency and whole major units — or `null` when it cannot be known yet.
 *
 * The one place the form applies the seat multiplier to a whole order, so
 * the payment-mode preview, the too-small guards and the payment-method
 * picker cannot disagree about what the invoice is worth.
 *
 * It mirrors the server's *effective* order: **round the unit, then
 * multiply**, then sum the courses.
 *
 * That is not what `line_item_total` looks like in isolation — it reads
 * `round_money(unit_price * seats)`, which suggests multiply-then-round.
 * But the unit it receives has already been rounded to a whole major unit
 * by `_convert_invoice_price` (identity pair -> `round_money(price)`;
 * cross-currency -> `convert().converted`, itself `round_money(amount *
 * rate)`), so its own rounding is a no-op and the real order is
 * round-then-multiply. Verified against the server: a `800.40` unit at 5
 * seats bills **4000**, not 4002.
 *
 * The distinction is worth the paragraph — it is a whole unit either way,
 * which is exactly the margin that decides an inclusive payment-method
 * ceiling or a "too small to split" guard.
 *
 * Reads each row's `issuePrice` (its per-seat price already converted into
 * the issue currency, reported by `CoursePriceFields`) rather than `price`,
 * which is quoted in the row's own base currency. The form does not know
 * that currency — `baseCurrency` beside it is an *override* that is empty
 * on the normal create path — so summing `price` and labelling the result
 * with the issue currency is how a £800 × 4 order came to preview as a
 * "$400 / $400" deposit split.
 *
 * Returns `null` rather than a partial sum whenever a row has not reported
 * (no course picked, no price typed, a conversion still in flight, or a
 * non-numeric entry). Callers show "no total yet"; a partial sum would be a
 * confidently wrong number, which is what this exists to stop.
 */
export function lineItemsGross(
  lineItems: readonly { issuePrice?: string }[],
  seats: number,
): number | null {
  if (lineItems.length === 0) return null
  let gross = 0
  for (const li of lineItems) {
    const unit = li.issuePrice ?? ""
    // Checked before rounding, not folded into it: `roundMoneyPreview("")`
    // is `0`, not `null`, because `Number("")` is `0`. The null branch
    // alone would price a half-filled invoice off its filled rows.
    if (unit.trim() === "") return null
    const rounded = roundMoneyPreview(unit)
    if (rounded === null) return null
    gross += rounded * seats
  }
  return gross > 0 ? gross : null
}

